import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Photo } from "../components/Photo";
import { StatusBadge, DeadlineBadge } from "../components/StatusBadge";
import { Loading } from "../components/Loading";
import { fmtDate, fmtDateFull, fmtDateTime } from "../lib/dates";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, PlayCircle, ClipboardCheck, Pencil,
  XCircle, MessageSquarePlus, Clock, Trash2, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { StarRating, StarsInline } from "../components/StarRating";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [job, setJob] = useState(null);
  const [managers, setManagers] = useState([]);
  const [productTypes, setProductTypes] = useState([]);

  const [readyOpen, setReadyOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [rating, setRating] = useState(0);
  const [receivedQty, setReceivedQty] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate(-1);
    });
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isOwner) {
      api.get("/managers").then((r) => setManagers(r.data)).catch(() => {});
      api.get("/product-types").then((r) => setProductTypes(r.data)).catch(() => {});
    }
  }, [isOwner]);

  if (!job) return <div className="min-h-screen bg-[#FAF8F5]"><Loading /></div>;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doAccept = () => act(() => api.post(`/jobs/${id}/accept`), "Job accepted");
  const doStart = () => act(() => api.post(`/jobs/${id}/start`), "Production started");
  const doReady = async () => { await act(() => api.post(`/jobs/${id}/ready`), "Sent to owner for check"); setReadyOpen(false); };
  const doApprove = async (result) => {
    if (result === "APPROVED") {
      if (!rating || rating < 1) {
        toast.error("Please give a quality rating from 1 to 5 stars.");
        return;
      }
      const rq = parseInt(receivedQty, 10);
      if (!rq || rq < 1) {
        toast.error("Please enter the actual received quantity.");
        return;
      }
      if (rq > job.quantity) {
        toast.error(`Received quantity cannot be more than the job quantity (${job.quantity} pcs).`);
        return;
      }
    }
    await act(() => api.post(`/jobs/${id}/approve`, {
      result, note: approveNote || null,
      rating: result === "APPROVED" ? rating : null,
      received_quantity: result === "APPROVED" ? parseInt(receivedQty, 10) : null,
    }),
      result === "APPROVED" ? "Job approved & completed" : "Marked not approved");
    setApproveOpen(false); setApproveNote(""); setRating(0); setReceivedQty("");
  };
  const doCancel = () => act(() => api.post(`/jobs/${id}/cancel`), "Job cancelled");
  const doNote = async () => {
    if (!noteText.trim()) return;
    await act(() => api.post(`/jobs/${id}/notes`, { note: noteText }), "Note added");
    setNoteText("");
  };
  const openEdit = () => {
    setEdit({ product_name: job.product_name, product_type: job.product_type || "", quantity: job.quantity, start_date: job.start_date, deadline: job.deadline, instruction: job.instruction || "", manager_id: job.manager_id, sample_provided: !!job.sample_provided, sample_note: job.sample_note || "", reason: "" });
    setEditOpen(true);
  };
  const doEdit = async () => {
    if (!edit.reason.trim()) { toast.error("Please provide a reason for the change."); return; }
    await act(() => api.put(`/jobs/${id}`, { ...edit, quantity: parseInt(edit.quantity, 10) }), "Job updated");
    setEditOpen(false);
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job permanently deleted.");
      setDeleteOpen(false);
      navigate("/owner/jobs", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const input = "w-full h-11 px-3 rounded-xl border border-slate-300 outline-none focus:border-[#C86D51] text-base bg-white";

  return (
    <div className="min-h-screen bg-[#FAF8F5] max-w-lg lg:max-w-3xl mx-auto pb-10">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-xl hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="tnum font-display text-lg font-bold">{job.job_number}</h1>
        </div>
        {isOwner && job.status !== "COMPLETED" && job.status !== "CANCELLED" && (
          <button data-testid="admin-edit-button" onClick={openEdit} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600"><Pencil className="h-4.5 w-4.5" /></button>
        )}
      </header>

      <Photo src={job.photo} className="h-64 w-full" />

      <div className="px-4 py-4 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <StatusBadge status={job.status} />
          <DeadlineBadge job={job} />
        </div>

        <div>
          <h2 className="font-display text-2xl font-black text-slate-900">{job.product_name}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {job.product_type && (
              <span data-testid="job-product-type" className="text-[10px] font-bold uppercase tracking-wide text-[#9A462B] bg-[#F9EFEA] border border-[#E6A893] rounded-md px-2 py-0.5">{job.product_type}</span>
            )}
            {isOwner && job.manager_name && <p className="text-sm text-slate-500">Manager: {job.manager_name}</p>}
          </div>
        </div>

        {/* Key facts */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 grid grid-cols-2 gap-y-4 gap-x-3">
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Job Quantity</p>
            <p className="tnum text-3xl font-black text-slate-900 leading-none mt-0.5">{job.quantity}</p>
          </div>
          {job.status === "COMPLETED" && job.received_quantity != null ? (
            <div>
              <p className="text-[10px] font-bold uppercase text-emerald-600">Received Quantity</p>
              <p className="tnum text-3xl font-black text-emerald-700 leading-none mt-0.5" data-testid="job-received-quantity">{job.received_quantity}</p>
            </div>
          ) : (
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-400">Deadline</p>
              <p className="tnum text-2xl font-black text-slate-900 leading-none mt-0.5">{fmtDate(job.deadline)}</p>
            </div>
          )}
          {job.status === "COMPLETED" && job.received_quantity != null && (
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-400">Deadline</p>
              <p className="tnum text-2xl font-black text-slate-900 leading-none mt-0.5">{fmtDate(job.deadline)}</p>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Start Date</p>
            <p className="tnum font-bold text-slate-800">{fmtDateFull(job.start_date)}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400">Status</p>
            <p className="font-bold text-slate-800">{job.status}</p>
          </div>
        </div>

        {/* Timeline */}
        {(job.ready_at || job.completion_date) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2 text-sm">
            {job.ready_at && (
              <div className="flex justify-between"><span className="text-slate-500">Ready for Owner Check</span><span className="tnum font-bold text-indigo-700">{fmtDateTime(job.ready_at)}</span></div>
            )}
            {job.completion_date && (
              <div className="flex justify-between"><span className="text-slate-500">Owner Completed</span><span className="tnum font-bold text-emerald-700">{fmtDateTime(job.completion_time)}</span></div>
            )}
          </div>
        )}

        {job.instruction && (
          <div className="bg-[#F9EFEA] border border-[#E6A893] rounded-2xl p-4">
            <p className="text-[10px] font-bold uppercase text-[#9A462B] mb-1">Owner Instruction</p>
            <p className="text-sm text-slate-700">{job.instruction}</p>
          </div>
        )}

        {/* Sample provided */}
        <div data-testid="sample-status" className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[10px] font-bold uppercase text-slate-400 mb-0.5">Sample</p>
          {job.sample_provided ? (
            <p className="text-base font-bold text-emerald-600">✅ PROVIDED</p>
          ) : (
            <p className="text-base font-bold text-slate-500">— NOT PROVIDED</p>
          )}
          {job.sample_provided && job.sample_note && <p className="text-sm text-slate-600 mt-1">{job.sample_note}</p>}
        </div>

        {/* With Earrings */}
        <div data-testid="with-earrings-status" className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[10px] font-bold uppercase text-slate-400 mb-0.5">With Earrings</p>
          {job.with_earrings ? (
            <p className="text-base font-bold text-emerald-600">✅ YES — includes earrings</p>
          ) : (
            <p className="text-base font-bold text-slate-500">— NO</p>
          )}
        </div>

        {/* Quality rating (owner only) */}
        {isOwner && job.status === "COMPLETED" && job.quality_rating && (
          <div data-testid="job-quality-block" className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">Owner Quality Rating</p>
            <div className="flex items-center gap-2">
              <StarsInline value={job.quality_rating} size="h-5 w-5" />
              <span className="tnum font-black text-amber-600">{job.quality_rating} / 5</span>
            </div>
            {job.quality_note && <p className="text-sm text-slate-600 mt-1">{job.quality_note}</p>}
          </div>
        )}

        {/* Deadline history */}
        {job.deadline_history?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Deadline Changes</p>
            {job.deadline_history.map((h, i) => (
              <p key={i} className="text-xs text-slate-600 mb-1">{fmtDate(h.old)} → <b>{fmtDate(h.new)}</b> · {h.reason} <span className="text-slate-400">({h.changed_by})</span></p>
            ))}
          </div>
        )}

        {/* MANAGER ACTIONS */}
        {!isOwner && (
          <div className="space-y-3">
            {job.status === "NEW" && (
              <button data-testid="accept-job-button" onClick={doAccept} disabled={busy}
                className="w-full py-4 rounded-xl copper-gradient text-white font-bold text-lg shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> Accept Job
              </button>
            )}
            {job.status === "ACCEPTED" && (
              <button data-testid="start-production-button" onClick={doStart} disabled={busy}
                className="w-full py-4 rounded-xl copper-gradient text-white font-bold text-lg shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <PlayCircle className="h-5 w-5" /> Start Production
              </button>
            )}
            {job.status === "IN PRODUCTION" && (
              <button data-testid="ready-for-check-button" onClick={() => setReadyOpen(true)} disabled={busy}
                className="w-full py-4 rounded-xl bg-indigo-600 text-white font-bold text-lg shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <ClipboardCheck className="h-5 w-5" /> Ready for Owner Check
              </button>
            )}
            {job.status === "READY FOR OWNER CHECK" && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 text-center">
                <p className="font-bold text-indigo-700">🔍 Waiting for Owner physical check</p>
                <p className="text-sm text-indigo-500 mt-1">The owner will approve & complete this job.</p>
              </div>
            )}
          </div>
        )}

        {/* OWNER ACTIONS */}
        {isOwner && (
          <div className="space-y-3">
            {job.status === "READY FOR OWNER CHECK" && (
              <button data-testid="open-quality-check-button" onClick={() => setApproveOpen(true)}
                className="w-full py-4 rounded-xl bg-indigo-600 text-white font-bold text-lg shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <ClipboardCheck className="h-5 w-5" /> Quality Check
              </button>
            )}
            {job.status !== "COMPLETED" && job.status !== "CANCELLED" && (
              <button data-testid="cancel-job-button" onClick={doCancel} disabled={busy}
                className="w-full py-3 rounded-xl bg-white border border-red-200 text-red-600 font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <XCircle className="h-5 w-5" /> Cancel Job
              </button>
            )}
            <button data-testid="delete-job-button" onClick={() => setDeleteOpen(true)} disabled={busy}
              className="w-full py-3 rounded-xl bg-red-600 text-white font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 shadow-sm">
              <Trash2 className="h-5 w-5" /> Delete Permanently
            </button>
          </div>
        )}

        {/* Approvals history */}
        {job.approvals?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Owner Decisions</p>
            {job.approvals.map((a) => (
              <div key={a.id} className="text-sm mb-2">
                <span className={`font-bold ${a.result === "APPROVED" ? "text-emerald-600" : "text-red-600"}`}>{a.result === "APPROVED" ? "✅ Approved" : "❌ Not Approved"}</span>
                {a.note && <span className="text-slate-600"> — {a.note}</span>}
                <p className="text-xs text-slate-400">{fmtDateTime(a.created_at)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Notes</p>
          <div className="space-y-2 mb-3">
            {job.notes?.length === 0 ? <p className="text-sm text-slate-400">No notes yet.</p> : job.notes.map((n) => (
              <div key={n.id} className="text-sm">
                <span className={`font-bold ${n.role === "OWNER" ? "text-[#9A462B]" : "text-indigo-600"}`}>{n.user_name}:</span>{" "}
                <span className="text-slate-700">{n.note}</span>
                <p className="text-xs text-slate-400">{fmtDateTime(n.created_at)}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" className="flex-1 h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-[#C86D51] text-sm bg-white" />
            <button data-testid="add-note-button" onClick={doNote} className="px-4 rounded-xl copper-gradient text-white"><MessageSquarePlus className="h-5 w-5" /></button>
          </div>
        </div>
      </div>

      {/* Ready confirm */}
      <AlertDialog open={readyOpen} onOpenChange={setReadyOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Send to Owner for check?</AlertDialogTitle>
            <AlertDialogDescription>Production is complete. Send this job to the owner for final checking.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ready-button" onClick={doReady} className="bg-indigo-600">Yes, Send to Owner</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Owner quality check dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Quality Check</DialogTitle>
            <DialogDescription>Physically inspect the jewellery, then decide.</DialogDescription>
          </DialogHeader>
          <Photo src={job.photo} className="h-36 w-full rounded-xl" />
          <div className="text-sm text-slate-600">{job.product_name}{job.product_type ? ` · ${job.product_type}` : ""} · Deadline {fmtDate(job.deadline)}</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
              <p className="text-[10px] font-bold uppercase text-slate-400">Job Quantity</p>
              <p className="tnum text-2xl font-black text-slate-900">{job.quantity}</p>
              <p className="text-[10px] text-slate-400">pcs ordered</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
              <p className="text-[10px] font-bold uppercase text-emerald-600">Received Qty *</p>
              <input data-testid="received-quantity-input" type="number" min="1" max={job.quantity} value={receivedQty}
                onChange={(e) => setReceivedQty(e.target.value)} placeholder="pcs"
                className="w-full h-9 mt-0.5 px-2 rounded-lg border border-emerald-300 text-center text-lg font-black outline-none focus:border-emerald-500 bg-white" />
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-bold uppercase text-amber-700 text-center mb-2">Quality Rating</p>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <textarea value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder="Note (optional)" rows="2" className="w-full p-3 rounded-xl border border-slate-300 outline-none focus:border-[#C86D51] text-sm bg-white" />
          <div className="grid grid-cols-1 gap-2">
            <button data-testid="approve-qc-button" onClick={() => doApprove("APPROVED")} disabled={busy} className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold">✅ Approve & Complete</button>
            <button data-testid="reject-qc-button" onClick={() => doApprove("NOT_APPROVED")} disabled={busy} className="w-full py-3.5 rounded-xl bg-white border border-red-300 text-red-600 font-bold">❌ Not Approved</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Admin Edit</DialogTitle>
            <DialogDescription>Changes are recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-bold uppercase text-slate-400">Product</label><input className={input} value={edit.product_name || ""} onChange={(e) => setEdit({ ...edit, product_name: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-slate-400">Product Type</label>
              <select data-testid="edit-product-type" className={input} value={edit.product_type || ""} onChange={(e) => setEdit({ ...edit, product_type: e.target.value })}>
                <option value="">Select type…</option>
                {edit.product_type && !productTypes.some((t) => t.name === edit.product_type) && (
                  <option value={edit.product_type}>{edit.product_type}</option>
                )}
                {productTypes.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-slate-400">Quantity</label><input data-testid="edit-quantity-input" type="number" className={input} value={edit.quantity || ""} onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-slate-400">Manager</label>
                <select className={input} value={edit.manager_id || ""} onChange={(e) => setEdit({ ...edit, manager_id: e.target.value })}>
                  {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-bold uppercase text-slate-400">Start</label><input type="date" className={input} value={edit.start_date || ""} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-slate-400">Deadline</label><input data-testid="edit-deadline-input" type="date" className={input} value={edit.deadline || ""} onChange={(e) => setEdit({ ...edit, deadline: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-bold uppercase text-slate-400">Instruction</label><textarea rows="2" className="w-full p-3 rounded-xl border border-slate-300 outline-none focus:border-[#C86D51] text-sm bg-white" value={edit.instruction || ""} onChange={(e) => setEdit({ ...edit, instruction: e.target.value })} /></div>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox data-testid="edit-sample-checkbox" checked={!!edit.sample_provided} onCheckedChange={(v) => setEdit({ ...edit, sample_provided: !!v })} className="h-5 w-5" />
                <span className="text-sm font-bold text-slate-800">Sample Provided</span>
              </label>
              {edit.sample_provided && (
                <input data-testid="edit-sample-note-input" className={input} value={edit.sample_note || ""} onChange={(e) => setEdit({ ...edit, sample_note: e.target.value })} placeholder="Sample note (optional)" />
              )}
            </div>
            <div><label className="text-xs font-bold uppercase text-red-500">Reason (required)</label><input data-testid="edit-reason-input" className={input} value={edit.reason || ""} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} placeholder="Why are you changing this?" /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-edit-button" onClick={doEdit} disabled={busy} className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold">Save Changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner permanent delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Delete this job permanently?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  <b className="tnum text-slate-800">{job.job_number}</b> · {job.product_name}
                  {job.product_type ? ` · ${job.product_type}` : ""} · {job.quantity} pcs · <b>{job.status}</b>
                </p>
                <p>
                  This will remove the job from the app <b>permanently</b> and it will no longer count in
                  any dashboard, monthly report, product-type breakdown, received-quantity total,
                  on-time / breach %, or quality score — including for past months.
                </p>
                <p className="font-bold text-red-600">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Keep Job</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white">
              Yes, Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
